import type { InvoiceFromOrderPreview } from "@kundeninfo/api-client";
import { formatBetrag, formatDatum } from "../format.js";

/**
 * TEXTE UND REGELN der Rückfrage vor „Rechnung erzeugen“ an einem Auftrag mit offenen Zahlplan-Posten (Spec 2026-11-02
 * „Rechnung erzeugen an Aufträgen mit Zahlplan: Rückfrage ohne Doppelzählung“, Oberfläche › Texte; Festlegungen P13,
 * P14). Muster `liquidityTexts.ts`: Wortlaut nur hier, Schlüssel englisch.
 *
 * Die Domäne hat ihre eigene Tabelle (`INVOICE_FROM_ORDER_TEXT`, Sätze der API und die Herkunft im Vorschlag); web
 * importiert sie nicht (Grenze `npm run boundaries`). `decisionRequired` und `chargesTaken` stehen deshalb hier ein
 * zweites Mal — die Maske wählt ihren Satz nach dem Fehlercode, nicht nach dem Text der Antwort.
 *
 * Beträge stehen netto (`formatBetrag` mit „netto“), wie im Kasten „Geplante Fälligkeiten“ und im Soll/Ist.
 */

type Period = InvoiceFromOrderPreview["periods"][number];

/** P14: „2026“ für ein Kalenderjahr (01.01.–31.12.), sonst „15.03.2026–14.03.2027“ (Vertragsjahr, Rumpfjahr). */
export function periodLabel(p: { periodStart: string; periodEnd: string }): string {
  const year = p.periodStart.slice(0, 4);
  return p.periodStart === `${year}-01-01` && p.periodEnd === `${year}-12-31`
    ? year : `${formatDatum(p.periodStart)}–${formatDatum(p.periodEnd)}`;
}

/**
 * P13: der Stichtag von Weg (b) — die früheste Fälligkeit der offenen Posten aller wählbaren Jahre, nie vor `today`.
 * `today` ist die Stichtag-Vorgabe des Rechnungsvorschlags (`defaultCutoffDate`, PF9), damit beide Wege denselben Tag
 * meinen. Ohne Jahre bleibt es bei `today`.
 */
export function proposalCutoff(preview: InvoiceFromOrderPreview, today: string): string {
  const earliest = preview.periods.map((p) => p.firstDueDate).sort()[0];
  return earliest !== undefined && earliest > today ? earliest : today;
}

const net = (amount: string): string => `${formatBetrag(Number(amount), "EUR")} netto`;
/** „fällig 01.12.2026“ für einen Posten, „fällig ab 01.03.2026“ für mehrere. */
const due = (p: Period): string => `fällig${p.openCount === 1 ? "" : " ab"} ${formatDatum(p.firstDueDate)}`;

export const INVOICE_FROM_ORDER_WEB_TEXT = {
  title: "Welche Zahlplan-Posten ersetzt die Rechnung?",
  periodLegend: "Leistungsjahr",
  create: "Rechnung erzeugen",
  propose: "Stattdessen vorschlagen",
  failed: "Die Rechnung konnte nicht erzeugt werden.",
  chargesTaken: "Die Posten dieses Leistungsjahres gehören inzwischen zu einer anderen Rechnung.",
  decisionRequired:
    "Zu diesem Auftrag sind Zahlplan-Posten offen. Bitte wählen, welche die Rechnung ersetzt, oder die Rechnung vorschlagen.",
  /** Rechnungsvorschlag (Review Aufgabe 3, G3 b): Zeilen da, aber alle schlagen 0,00 vor. */
  nothingDue: "Zu diesem Stichtag ist nichts fällig. Ein Stichtag am Fälligkeitstag (Spalte Herkunft) führt die "
    + "Zahlplan-Posten mit.",
  oneYear: (p: Period, invoiceNet: string): string => (p.openCount === 1
    ? `Im Leistungsjahr ${periodLabel(p)} ist 1 Zahlplan-Posten offen (${net(p.openAmount)}, ${due(p)}). Die Rechnung `
      + `übernimmt alle Positionen (${net(invoiceNet)}) und tritt an seine Stelle; in Liquidität und Fälligkeiten zählt `
      + "dann nur noch die Rechnung. Der Entwurf lässt sich danach nicht mehr entfernen."
    : `Im Leistungsjahr ${periodLabel(p)} sind ${p.openCount} Zahlplan-Posten offen (${net(p.openAmount)}, ${due(p)}). `
      + `Die Rechnung übernimmt alle Positionen (${net(invoiceNet)}) und tritt an ihre Stelle; in Liquidität und `
      + "Fälligkeiten zählt dann nur noch die Rechnung. Der Entwurf lässt sich danach nicht mehr entfernen."),
  severalYears: (count: number, invoiceNet: string): string =>
    `Zu diesem Auftrag sind Zahlplan-Posten in ${count} Leistungsjahren offen. Die Rechnung übernimmt alle Positionen `
    + `(${net(invoiceNet)}) und tritt an die Stelle der Posten des gewählten Leistungsjahres. Der Entwurf lässt sich danach nicht mehr entfernen.`,
  periodOption: (p: Period): string =>
    `${periodLabel(p)} · ${p.openCount} Posten · ${net(p.openAmount)} · ${due(p)}`,
  invoicedHint: (p: Period): string =>
    `Im Leistungsjahr ${periodLabel(p)} ${p.invoicedCount === 1 ? "ist" : "sind"} schon ${p.invoicedCount} Posten `
    + "abgerechnet. Die Rechnung enthält trotzdem alle Positionen voll; bitte im Entwurf anpassen.",
  inDraftHint: (p: Period): string => (p.inDraftCount === 1
    ? "1 Posten dieses Leistungsjahres gehört schon zu einem Rechnungsentwurf und bleibt dort."
    : `${p.inDraftCount} Posten dieses Leistungsjahres gehören schon zu Rechnungsentwürfen und bleiben dort.`),
  receipt: (p: Period): string =>
    `Rechnungsentwurf erzeugt. Er ersetzt ${p.openCount} Zahlplan-Posten aus ${periodLabel(p)}.`,
} as const;
